import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineAdjustmentsHorizontal,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClipboardDocumentCheck,
  HiOutlinePrinter,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi2";
import {
  authService,
  rcaService,
  machineService,
  masterDataService,
  plantService,
} from "../services";
import { usePermissions } from "../hooks/usePermissions";
import { toast } from "react-toastify";
import SharedModal from "../components/common/SharedModal";
import PageLoading from "../components/common/PageLoading";
import "./RCA.css";

// Helper function to get cached data (defined outside component for synchronous access)
const getCachedRCAData = () => {
  try {
    const cachedRcaList = sessionStorage.getItem("rca_records_data");
    const cachedMachines = sessionStorage.getItem("machines_data");
    const cachedStatuses = sessionStorage.getItem("rca_statuses_data");

    return {
      rcaList: cachedRcaList ? JSON.parse(cachedRcaList) : [],
      machines: cachedMachines ? JSON.parse(cachedMachines) : [],
      statuses: cachedStatuses ? JSON.parse(cachedStatuses) : [],
      hasAll: !!(cachedRcaList && cachedMachines && cachedStatuses),
    };
  } catch (e) {
    return { rcaList: [], machines: [], statuses: [], hasAll: false };
  }
};

const RCA = () => {
  const navigate = useNavigate();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("RCA.View");
  const canEdit = hasPermission("RCA.Edit");
  const canDelete = hasPermission("RCA.Delete");

  console.log("canView", canView);
  console.log("canEdit", canEdit);
  console.log("canDelete", canDelete);

  // Initialize state with cached data if available (synchronous)
  const cachedData = getCachedRCAData();
  const [rcaList, setRcaList] = useState(cachedData.rcaList);
  const [machines, setMachines] = useState(cachedData.machines);
  const [statuses, setStatuses] = useState(cachedData.statuses);

  const [loading, setLoading] = useState(!cachedData.hasAll);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({
    plantId: "",
    statusId: "",
    machineId: "",
  });
  const [plants, setPlants] = useState([]);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Refresh cached data on mount (in case it was updated elsewhere)
  useEffect(() => {
    if (tenantId) {
      const refreshedCachedData = getCachedRCAData();
      if (refreshedCachedData.hasAll) {
        setRcaList(refreshedCachedData.rcaList);
        setMachines(refreshedCachedData.machines);
        setStatuses(refreshedCachedData.statuses);
        setLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      const msg = "Please login to manage RCA records";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    // Check if we have cached data - if yes, show page immediately
    const hasCachedData =
      sessionStorage.getItem("rca_records_data") &&
      sessionStorage.getItem("machines_data") &&
      sessionStorage.getItem("rca_statuses_data");

    if (hasCachedData) {
      setLoading(false);
      if (canView) {
        loadInitialData();
      }
    }

    if (!canView) {
      const msg = "You do not have permission to view RCA records";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (canView && !hasCachedData) {
      loadInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canView]);

  const loadInitialData = async () => {
    try {
      // Only show loading if we don't have cached data
      const hasCachedData = sessionStorage.getItem("rca_records_data");
      if (!hasCachedData) {
        setLoading(true);
      }
      setError("");

      const [rcaData, machinesData, statusesData, plantsData] = await Promise.all([
        rcaService.getRcaRecords(),
        machineService.getAllMachines(),
        masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
        plantService.getAllPlants(),
      ]);

      setPlants(plantsData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      setRcaList(rcaData || []);
      setMachines(machinesData || []);

      const rcaStatuses = statusesData.filter(
        (s) => !s.statusType || s.statusType === "RCA",
      );
      setStatuses(rcaStatuses);

      // Cache the data
      sessionStorage.setItem("rca_records_data", JSON.stringify(rcaData || []));
      sessionStorage.setItem(
        "machines_data",
        JSON.stringify(machinesData || []),
      );
      sessionStorage.setItem("rca_statuses_data", JSON.stringify(rcaStatuses));
    } catch (err) {
      console.error("Error loading RCA records:", err);
      const msg = "Failed to load RCA records. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchRcaWithFilters = async () => {
    try {
      setRefreshing(true);
      setError("");

      const data = await rcaService.getRcaRecords(
        filters.machineId ? parseInt(filters.machineId, 10) : null,
        filters.statusId ? parseInt(filters.statusId, 10) : null,
      );
      setRcaList(data || []);
    } catch (err) {
      console.error("Error fetching RCA records:", err);
      setError("Failed to fetch RCA records. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchRcaWithFilters();
    setShowFilterDrawer(false);
  };

  const clearFilters = () => {
    setFilters({
      plantId: "",
      statusId: "",
      machineId: "",
    });
    fetchRcaWithFilters();
  };

  const getMachineName = (machineId) => {
    if (!machineId) return "-";
    const m = machines.find((x) => x.machineId === machineId);
    return m ? `${m.machineName} (${m.machineCode})` : `Machine ${machineId}`;
  };

  const getStatusName = (statusId) => {
    if (!statusId) return "-";
    const s = statuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return d.toLocaleString();
  };

  const filteredRcaList = useMemo(() => {
    return rcaList.filter((rca) => {
      const matchesStatus =
        !filters.statusId || rca.statusId?.toString() === filters.statusId;

      const matchesPlant = 
        !filters.plantId || rca.plantId?.toString() === filters.plantId;

      const matchesMachine =
        !filters.machineId || rca.machineId?.toString() === filters.machineId;

      return matchesStatus && matchesPlant && matchesMachine;
    });
  }, [rcaList, filters]);

  const handleDelete = async () => {
    if (!canDelete || !deleteConfirm) {
      setError("You do not have permission to delete RCA records");
      return;
    }
    try {
      const toastId = toast.loading("Deleting RCA record...");
      await rcaService.deleteRcaRecord(deleteConfirm.rcaId);
      await fetchRcaWithFilters();
      setDeleteConfirm(null);
      toast.update(toastId, { render: "RCA record deleted successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error("Error deleting RCA record:", err);
      const msg = "Failed to delete RCA record. Please try again.";
      setError(msg);
      toast.error(msg, { closeButton: true });
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer rca-page">
        <PageLoading message="Loading RCA records..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer rca-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Root Cause Analysis (RCAA)</h1>
          <p>
            Capture and track root cause investigations using Fishbone Diagram
            and 5 Whys analysis.
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary btn"
            onClick={() => navigate("/home/rca/five-whys")}
            title="5 Whys Analysis"
          >
            <HiOutlineQuestionMarkCircle size={20} /> 5 Whys
          </button>
          <button
            className="btn-secondary"
            onClick={clearFilters}
            disabled={refreshing}
          >
            <HiOutlineClipboardDocumentCheck
              className={refreshing ? "spinning" : ""}
            />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">

          <div className="filters-row justify-content-between">
            <div>&nbsp;</div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="filter-toggle"
                type="button"
                onClick={() => setShowFilterDrawer(true)}
              >
                <HiOutlineAdjustmentsHorizontal />
                <span>Filters</span>
              </button>
              <div className="table-info">
                {filteredRcaList.length}{" "}
                {filteredRcaList.length === 1 ? "record" : "records"}
              </div>
            </div>
          </div>
        </div>

        {canView && filteredRcaList.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Trigger</th>
                <th>Problem</th>
                <th>Root Cause</th>
                <th>Method</th>
                <th>Machine</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {filteredRcaList.map((rca) => (
                <tr key={rca.rcaId}>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          title="Open RCA"
                          onClick={() => {
                            const method = rca.method?.toLowerCase() || "";
                            if (
                              method.includes("5 whys") ||
                              method === "5 whys"
                            ) {
                              navigate(
                                `/home/rca/five-whys?rcaId=${rca.rcaId}`,
                              );
                            } else if (
                              method.includes("fishbone") ||
                              method === "fishbone"
                            ) {
                              navigate(
                                `/home/rca/fishbone?rcaId=${rca.rcaId}`,
                              );
                            } else {
                              navigate(`/home/rca-entry/${rca.rcaId}`);
                            }
                          }}
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      <button
                        className="btn-icon-generate"
                        title="Generate CAPA"
                        onClick={() =>
                          navigate(`/home/capa-entry?rcaId=${rca.rcaId}`)
                        }
                      >
                        <HiOutlineClipboardDocumentCheck />
                      </button>
                      <button
                        className="btn-icon-print"
                        title="Print RCA"
                        onClick={() => {
                          const method = rca.method?.toLowerCase() || "";
                          if (
                            method.includes("5 whys") ||
                            method === "5 whys"
                          ) {
                            navigate(
                              `/home/rca/five-whys?rcaId=${rca.rcaId}`,
                              {
                                state: { autoPrint: true },
                              },
                            );
                           } else {
                             // Fallback or other methods if they support print in future
                             toast.info("Print preview is currently only available for 5 Whys Analysis.");
                           }
                         }}
                       >
                        <HiOutlinePrinter />
                      </button>
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          title="Delete RCA"
                          onClick={() => {
                            setDeleteConfirm(rca);
                          }}
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                  <td>{rca.triggerReason || "-"}</td>
                  <td>
                    {rca.problemDesc
                      ? rca.problemDesc.length > 80
                        ? `${rca.problemDesc.substring(0, 80)}...`
                        : rca.problemDesc
                      : "-"}
                  </td>
                  <td>
                    {(() => {
                      if (!rca.rootCause) return "-";

                      try {
                        let displayContent = "";
                        const content = rca.rootCause;

                        // Extract the 5 Whys Analysis section
                        const fiveWhysMatch = content.match(/5 Whys Analysis:\s*([\s\S]*?)(?:\n\nRecommended Solution:|$)/);

                        if (fiveWhysMatch && fiveWhysMatch[1]) {
                          // Get all the "Why" lines
                          const whyLines = fiveWhysMatch[1]
                            .split('\n')
                            .filter(line => line.trim().startsWith('Why'))
                            .map(line => line.replace(/^Why \d+:\s*/, '').trim());

                          // Get the last "Why" answer as the root cause
                          if (whyLines.length > 0) {
                            displayContent = whyLines[whyLines.length - 1];
                          }
                        }

                        // Fallback: if no 5 Whys found, try to extract clean text
                        if (!displayContent) {
                          const metadataHeaders = [
                            "Loss Name:",
                            "Immediate Action:",
                            "Action Conclusion:",
                            "Spare Part Replacement:",
                            "Shift:",
                            "Reference No:",
                            "Root Cause Validation:",
                            "Action Items:",
                            "Kaizen Idea:",
                            "5 Whys Analysis:",
                            "Recommended Solution:"
                          ];

                          const lines = content.split("\n");
                          const contentLines = lines.filter(
                            (line) => {
                              const trimmedLine = line.trim();
                              // Skip empty lines, metadata headers, and JSON content
                              if (!trimmedLine) return false;
                              if (trimmedLine.startsWith('{') || trimmedLine.startsWith('[')) return false;
                              if (metadataHeaders.some((header) => trimmedLine.startsWith(header))) return false;
                              return true;
                            }
                          );

                          displayContent = contentLines.join(" ").trim();
                        }

                        // Final fallback
                        if (!displayContent) displayContent = rca.rootCause;

                        return displayContent.length > 80
                          ? `${displayContent.substring(0, 80)}...`
                          : displayContent;
                      } catch (error) {
                        console.error("Error parsing root cause:", error);
                        return rca.rootCause.length > 80
                          ? `${rca.rootCause.substring(0, 80)}...`
                          : rca.rootCause;
                      }
                    })()}
                  </td>
                  <td>
                    <span className="method-pill">
                      {rca.method || "Not specified"}
                    </span>
                  </td>
                  <td>{getMachineName(rca.machineId)}</td>
                  <td>
                    <span className="status-pill">
                      {getStatusName(rca.statusId)}
                    </span>
                  </td>
                  <td>{formatDateTime(rca.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Filter Drawer */}
      {showFilterDrawer && (
        <div
          className="filter-drawer-overlay"
          onClick={() => setShowFilterDrawer(false)}
        >
          <div className="filter-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="filter-drawer-header">
              <h3>Filters</h3>
              <button
                className="btn-icon"
                onClick={() => setShowFilterDrawer(false)}
              >
                <HiOutlineXMark />
              </button>
            </div>
            <div className="filter-drawer-content">
              <div className="form-group">
                <label>Plant</label>
                <select
                  name="plantId"
                  value={filters.plantId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Plants</option>
                  {plants.map((plant) => (
                    <option key={plant.plantId} value={plant.plantId}>
                      {plant.plantName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="statusId"
                  value={filters.statusId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {statuses.map((status) => (
                    <option key={status.statusId} value={status.statusId}>
                      {status.statusName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Machine</label>
                <select
                  name="machineId"
                  value={filters.machineId}
                  onChange={handleFilterChange}
                >
                  <option value="">All Machines</option>
                  {machines.map((m) => (
                    <option key={m.machineId} value={m.machineId}>
                      {m.machineName} ({m.machineCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filter-drawer-footer">
              <button className="btn-secondary btn" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn-primary btn" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete RCA"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary btn"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger btn"
              onClick={handleDelete}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete this RCA record? This action
            cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default RCA;
